
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { TimeEntry } from "@/components/TimeEntryForm";
import { useProjects } from "./useProjects";

export function useTimeEntries() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { projects } = useProjects();

  const { data: entries = [], isLoading: isLoadingEntries } = useQuery({
    queryKey: ['time-entries'],
    queryFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('time_entries')
        .select('*')
        .eq('user_id', user.user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data.map(entry => ({
        id: entry.id,
        date: new Date(entry.date),
        minutes: entry.minutes,
        projectId: entry.project_id,
        description: entry.description,
        is_billable: entry.is_billable ?? true
      }));
    }
  });

  const addEntryMutation = useMutation({
    mutationFn: async (entry: TimeEntry) => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('time_entries')
        .insert({
          user_id: user.user.id,
          project_id: entry.projectId,
          description: entry.description,
          minutes: entry.minutes,
          date: entry.date.toISOString(),
          is_billable: entry.is_billable ?? true
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-entries'] });
    }
  });

  const updateEntryMutation = useMutation({
    mutationFn: async ({ id, entry }: { id: string; entry: Partial<TimeEntry> }) => {
      const { data, error } = await supabase
        .from('time_entries')
        .update({
          project_id: entry.projectId,
          description: entry.description,
          minutes: entry.minutes,
          date: entry.date?.toISOString(),
          is_billable: entry.is_billable
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-entries'] });
    }
  });

  const deleteEntryMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('time_entries')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-entries'] });
    }
  });

  const handleAddEntry = async (entry: TimeEntry) => {
    try {
      await addEntryMutation.mutateAsync(entry);
      const project = projects.find(p => p.id === entry.projectId);
      toast({
        title: "Time Entry Added",
        description: `Added ${Math.floor(entry.minutes / 60)}h ${entry.minutes % 60}m to ${project?.name || 'project'}`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add time entry. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleUpdateEntry = async (id: string, entry: Partial<TimeEntry>) => {
    try {
      await updateEntryMutation.mutateAsync({ id, entry });
      const project = projects.find(p => p.id === entry.projectId);
      toast({
        title: "Time Entry Updated",
        description: `Updated entry${project ? ` for ${project.name}` : ''}`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update time entry. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDeleteEntry = async (id: string) => {
    try {
      await deleteEntryMutation.mutateAsync(id);
      toast({
        title: "Time Entry Deleted",
        description: "The time entry has been removed",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete time entry. Please try again.",
        variant: "destructive"
      });
    }
  };

  return {
    entries,
    isLoadingEntries,
    handleAddEntry,
    handleUpdateEntry,
    handleDeleteEntry
  };
}
